/**
 * Utility functions for statistical testing
 */

import { SignificanceLevel as TableSignificanceLevel } from "@store/slices/table-config-slice";
import { SignificanceLevel as CrosstabSignificanceLevel } from "@store/slices/crosstab-config-slice";
import { StatTestType } from "@models/Chart";

// Union type for all significance levels (they should be the same)
type SignificanceLevel = TableSignificanceLevel | CrosstabSignificanceLevel;

/**
 * Converts significance level percentage to p-value for API requests
 *
 * @param significanceLevel - The significance level as a percentage string ("90%", "95%", "99%")
 * @returns The corresponding p-value (0.1, 0.05, 0.01)
 *
 * @example
 * significanceLevelToPValue("90%") // returns 0.1
 * significanceLevelToPValue("95%") // returns 0.05
 * significanceLevelToPValue("99%") // returns 0.01
 */
export const significanceLevelToPValue = (
  significanceLevel: SignificanceLevel,
): number => {
  const mapping: Record<SignificanceLevel, number> = {
    "90%": 0.1, // NINETY_PERCENT - 90% confidence, 10% significance
    "95%": 0.05, // NINETY_FIVE_PERCENT - 95% confidence, 5% significance
    "99%": 0.01, // NINETY_NINE_PERCENT - 99% confidence, 1% significance
  };

  return mapping[significanceLevel];
};

/**
 * Converts p-value to significance level percentage for display
 *
 * @param pValue - The p-value (0.1, 0.05, 0.01)
 * @returns The corresponding significance level as a percentage string
 *
 * @example
 * pValueToSignificanceLevel(0.1) // returns "90%"
 * pValueToSignificanceLevel(0.05) // returns "95%"
 * pValueToSignificanceLevel(0.01) // returns "99%"
 */
export const pValueToSignificanceLevel = (
  pValue: number,
): SignificanceLevel => {
  const mapping: Record<number, SignificanceLevel> = {
    0.1: "90%",
    0.05: "95%",
    0.01: "99%",
  };

  return mapping[pValue] || "95%"; // Default to 95% if unknown value
};

/**
 * Converts test type from UI format to API format
 *
 * @param testType - The test type from UI ("z-test" or "t-test")
 * @returns The corresponding API test type ("Z-TEST" or "T-TEST")
 *
 * @example
 * testTypeToApiFormat("z-test") // returns "Z-TEST"
 * testTypeToApiFormat("t-test") // returns "T-TEST"
 */
export const testTypeToApiFormat = (
  testType: StatTestType | undefined,
): "Z-TEST" | "T-TEST" | undefined => {
  if (!testType) return undefined;

  const mapping: Record<StatTestType, "Z-TEST" | "T-TEST"> = {
    "z-test": "Z-TEST",
    "t-test": "T-TEST",
  };

  return mapping[testType];
};
